import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { useState, useRef, useEffect } from "react";
import "../../assets/styles/colors.css";

function TopologyActionsPanel({
  fileInputRef,
  onPreviewJson,
  onImportClick,
  onImportChange,
  onReset,
  onSubmitTopology,
  onSyncTopology,
  hasUnsyncedChanges = false,
  horizontal = false,
  savedTopologies = [],
  defaultTopologies = [],
  onSavedTopologyClick,
  onDefaultTopologyClick,
  aiLoading = false,
  submitLoading = false,
  submitSuccess = false,
  submitError = null,
  syncLoading = false,
  isRevertPreview = false,
  onRevertSave = null,
  onRevertUndo = null,
}) {
  const { t } = useTranslation();
  const [myTopologiesOpen, setMyTopologiesOpen] = useState(false);
  const [defaultTopologiesOpen, setDefaultTopologiesOpen] = useState(false);
  const myTopologiesRef = useRef(null);
  const defaultTopologiesRef = useRef(null);

  useEffect(() => {
    if (aiLoading) {
      setDefaultTopologiesOpen(false);
    }
  }, [aiLoading]);

  useEffect(() => {
    function handleClickOutside(event) {
      const clickedInsideMyTopologies =
        myTopologiesRef.current &&
        myTopologiesRef.current.contains(event.target);
      const clickedInsideDefaultTopologies =
        defaultTopologiesRef.current &&
        defaultTopologiesRef.current.contains(event.target);

      if (!clickedInsideMyTopologies) {
        setMyTopologiesOpen(false);
      }
      if (!clickedInsideDefaultTopologies) {
        setDefaultTopologiesOpen(false);
      }
    }
    if (myTopologiesOpen || defaultTopologiesOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [myTopologiesOpen, defaultTopologiesOpen]);

  const buttonClass = horizontal
    ? "inline-flex items-center justify-center gap-1 sm:gap-2 rounded-lg border border-[var(--color-primary)]/50 bg-transparent px-2 sm:px-3 py-2 sm:py-3 text-xs sm:text-sm text-[var(--color-text-white)] transition-colors duration-200 hover:border-[var(--color-primary)] hover:bg-[var(--color-primary)]/10 whitespace-nowrap"
    : "inline-flex items-center justify-center rounded-lg border border-[var(--color-primary)]/50 bg-transparent px-4 py-2.5 text-sm text-[var(--color-primary)] transition-colors duration-200 hover:border-[var(--color-primary)] hover:bg-[var(--color-primary)]/10";

  const submitButtonClass = horizontal
    ? "inline-flex items-center justify-center gap-1 sm:gap-2 rounded-lg border border-[var(--color-primary)] bg-[var(--color-primary)] px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-[var(--color-text-on-primary)] font-semibold transition-colors duration-200 hover:shadow-[0_0_10px_var(--color-shadow-primary-hover)] whitespace-nowrap"
    : "inline-flex items-center justify-center rounded-lg border border-[var(--color-primary)] bg-[var(--color-primary)] px-4 py-2.5 text-sm text-[var(--color-text-on-primary)] font-semibold transition-colors duration-200 hover:shadow-[0_0_10px_var(--color-shadow-primary-hover)]";

  if (horizontal) {
    return (
      <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] p-3 rounded-xl flex flex-wrap items-center justify-between gap-2 shadow-sm">
        {/* Left section: Import, Preview JSON, Reset, Sync topology */}
        <div className="flex items-center gap-2 p-1">
          <button
            type="button"
            onClick={onImportClick}
            className="p-2 hover:bg-[var(--color-primary)]/10 rounded-lg text-[var(--color-primary)] transition-colors"
            title={t("topology.import_topology")} data-tour="action-import"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
              />
            </svg>
          </button>
          <input
            type="file"
            accept="application/json"
            ref={fileInputRef}
            onChange={onImportChange}
            style={{ display: "none" }}
          />

          {onPreviewJson && (
            <button
              type="button"
              onClick={onPreviewJson}
              className="p-2 hover:bg-[var(--color-primary)]/10 rounded-lg text-[var(--color-primary)] transition-colors"
              title={t("topology.preview_json")} data-tour="action-preview-json"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                />
              </svg>
            </button>
          )}

          <div className="h-6 w-px bg-[var(--color-border)] mx-1" />

          <button
            type="button"
            onClick={onReset}
            className="p-2 hover:bg-[var(--color-primary)]/10 rounded-lg text-gray-400 transition-colors"
            title={t("topology.reset_canvas")} data-tour="action-reset"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
              />
            </svg>
          </button>

          {onSyncTopology && (
            <>
              <div className="h-6 w-px bg-[var(--color-border)] mx-1" />
              {isRevertPreview ? (
                <>
                  <button
                    type="button"
                    onClick={onRevertSave}
                    disabled={syncLoading}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors border-green-500/50 text-green-600 dark:text-green-400 hover:bg-green-500/10 hover:border-green-500"
                    title={t("Save reverted canvas as current")}
                  >
                    {syncLoading ? (
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                    )}
                    <span className="hidden sm:inline">{t("Save Changes")}</span>
                  </button>
                  <button
                    type="button"
                    onClick={onRevertUndo}
                    disabled={syncLoading}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors border-red-400/50 text-red-500 dark:text-red-400 hover:bg-red-500/10 hover:border-red-400"
                    title={t("Undo revert and restore previous canvas")}
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span className="hidden sm:inline">{t("Undo")}</span>
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={
                    hasUnsyncedChanges && !syncLoading
                      ? onSyncTopology
                      : undefined
                  }
                  disabled={!hasUnsyncedChanges || syncLoading}
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    !hasUnsyncedChanges || syncLoading
                      ? "opacity-40 cursor-default border-[var(--color-border)] text-[var(--color-text-muted)]"
                      : "border-[var(--color-primary)]/50 text-[var(--color-primary)] hover:bg-[var(--color-primary)]/10 hover:border-[var(--color-primary)]"
                  }`}
                  title={t("topology.sync.sync_topology_with_current_ai_session")} data-tour="action-sync"
                >
                  {syncLoading ? (
                    <>
                      <svg
                        className="w-4 h-4 animate-spin"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      <span className="hidden sm:inline">
                        {t("topology.sync.syncing")}
                      </span>
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={1.5}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9M20 20v-5h-.581m-15.357-2a8.003 8.003 0 0015.357 2"
                        />
                      </svg>
                      <span className="hidden sm:inline">
                        {t("topology.sync.sync_topology")}
                      </span>
                    </>
                  )}
                </button>
              )}
            </>
          )}
        </div>

        {/* Right section: Default/My Topologies dropdowns, Submit Topology */}
        <div className="flex items-center gap-3 text-xs font-medium text-slate-500 dark:text-gray-400">
          <div className="relative" ref={defaultTopologiesRef} data-tour="action-default-topologies">
            <button
              type="button"
              onClick={
                aiLoading
                  ? undefined
                  : () => setDefaultTopologiesOpen((prev) => !prev)
              }
              disabled={aiLoading}
              className={`inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] px-3 sm:px-4 py-2 text-sm font-medium text-[var(--color-text)] shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30 ${
                aiLoading
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:border-[var(--color-primary)]/50 hover:bg-[var(--color-bg)]"
              }`}
              title={
                aiLoading
                  ? t("Please wait until AI finishes building the topology")
                  : t("topology.default_topologies.default_topologies")
              }
            >
              <svg
                className="w-4 h-4 text-[var(--color-text)]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3.75 6.75A2.25 2.25 0 016 4.5h12a2.25 2.25 0 012.25 2.25v10.5A2.25 2.25 0 0118 19.5H6a2.25 2.25 0 01-2.25-2.25V6.75z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M8.25 9h7.5M8.25 12h7.5M8.25 15h4.5"
                />
              </svg>
              <span className="hidden sm:inline">
                {t("topology.default_topologies.default_topologies")}
              </span>
              <svg
                className={`w-4 h-4 text-[var(--color-text)] transition-transform ${defaultTopologiesOpen ? "rotate-180" : ""}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {defaultTopologiesOpen && (
              <div className="absolute right-0 top-full z-50 mt-2 w-fit min-w-56 max-w-[95vw] rounded-xl border border-(--color-border) bg-(--color-bg) py-2 shadow-[0_0_20px_var(--color-shadow-primary)]">
                <div className="border-b border-[var(--color-border)] px-4 py-2">
                  <span className="text-xs font-semibold text-[var(--color-text-muted)]">
                    {t("topology.default_topologies.available_defaults")}
                  </span>
                </div>
                <ul className="max-h-64 overflow-y-auto py-1">
                  {defaultTopologies.length === 0 ? (
                    <li className="px-4 py-3 text-center text-sm text-[var(--color-text-muted)]">
                      {t("topology.default_topologies.no_default_topologies")}
                    </li>
                  ) : (
                    defaultTopologies.map((item, index) => {
                      const label =
                        item.name ??
                        item.ai_title ??
                        item.title ??
                        item.id ??
                        t("topology.my_topologies.unnamed");
                      const id =
                        item.id ?? item.topology_id ?? item.thread_id ?? index;
                      return (
                        <li key={id}>
                          <button
                            type="button"
                            disabled={aiLoading}
                            className={`block w-full text-left whitespace-nowrap px-4 py-2.5 text-sm text-[var(--color-text)] transition-colors ${
                              aiLoading
                                ? "opacity-50 cursor-not-allowed"
                                : "hover:bg-[var(--color-primary)]/10 hover:text-[var(--color-primary)]"
                            }`}
                            onClick={() => {
                              if (aiLoading) return;
                              setDefaultTopologiesOpen(false);
                              if (onDefaultTopologyClick)
                                onDefaultTopologyClick(item);
                            }}
                          >
                            {label}
                          </button>
                        </li>
                      );
                    })
                  )}
                </ul>
              </div>
            )}
          </div>

          <div className="relative" ref={myTopologiesRef} data-tour="action-my-topologies">
            <button
              type="button"
              onClick={() => setMyTopologiesOpen((prev) => !prev)}
              className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] px-3 sm:px-4 py-2 text-sm font-medium text-[var(--color-text)] shadow-sm transition-colors hover:border-[var(--color-primary)]/50 hover:bg-[var(--color-bg)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30"
              title={t("topology.my_topologies.my_topologies")}
            >
              <svg
                className="w-4 h-4 text-[var(--color-text)]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M2.25 8.25A2.25 2.25 0 014.5 6h4.19c.596 0 1.17.237 1.591.659l.31.31c.422.422.995.659 1.591.659H19.5a2.25 2.25 0 012.25 2.25v7.875A2.625 2.625 0 0119.125 20.5H4.875A2.625 2.625 0 012.25 17.875V8.25z"
                />
              </svg>
              <span className="hidden sm:inline">
                {t("topology.my_topologies.my_topologies")}
              </span>
              <svg
                className={`w-4 h-4 text-[var(--color-text)] transition-transform ${myTopologiesOpen ? "rotate-180" : ""}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {myTopologiesOpen && (
              <div className="absolute right-0 top-full z-50 mt-2 w-fit min-w-56 max-w-[95vw] rounded-xl border border-[var(--color-border)] bg-(--color-bg) py-2 shadow-[0_0_20px_var(--color-shadow-primary)]">
                <div className="border-b border-(--color-border) px-4 py-2">
                  <span className="text-xs font-semibold text-(--color-text-muted)">
                    {t("topology.my_topologies.recent_topologies")}
                  </span>
                </div>
                <ul className="max-h-64 overflow-y-auto py-1">
                  {savedTopologies.length === 0 ? (
                    <li className="px-4 py-3 text-center text-sm text-[var(--color-text-muted)]">
                      {t("topology.my_topologies.no_topologies_yet")}
                    </li>
                  ) : (
                    savedTopologies.map((item, index) => {
                      const label =
                        item.name ?? item.title ?? item.id ?? t("Unnamed");
                      const id =
                        item.id ?? item.topology_id ?? item.thread_id ?? index;
                      return (
                        <li key={id}>
                          <button
                            type="button"
                            className="block w-full text-left whitespace-nowrap px-4 py-2.5 text-sm text-[var(--color-text)] hover:bg-[var(--color-primary)]/10 hover:text-[var(--color-primary)] transition-colors"
                            onClick={() => {
                              setMyTopologiesOpen(false);
                              if (onSavedTopologyClick)
                                onSavedTopologyClick(item);
                            }}
                          >
                            {label}
                          </button>
                        </li>
                      );
                    })
                  )}
                </ul>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={onSubmitTopology} data-tour="action-submit"
            disabled={submitLoading}
            className="px-4 sm:px-6 py-2 bg-[var(--color-primary)] hover:bg-[var(--color-primary)]/90 text-[var(--color-text-on-primary)] rounded-lg flex items-center gap-2 text-sm font-semibold transition-all shadow-lg disabled:opacity-70 disabled:cursor-wait"
          >
            {submitLoading ? (
              <>
                <svg
                  className="w-4 h-4 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span className="hidden sm:inline">
                  {t("generic.submitting")}
                </span>
              </>
            ) : submitSuccess ? (
              <>
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <span className="hidden sm:inline">
                  {t("generic.submitted")}
                </span>
              </>
            ) : submitError ? (
              <>
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                <span className="hidden sm:inline">{t("generic.failed")}</span>
              </>
            ) : (
              <>
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="hidden sm:inline">
                  {t("topology.submit_topology.submit")}
                </span>
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-card)] p-6 shadow-[0_0_20px_var(--color-shadow-primary)]">
      <h3 className="border-b border-[var(--color-border)] pb-4 text-lg font-semibold text-[var(--color-text-white)] mb-4">
        {t("topology.actions")}
      </h3>
      <div className="mt-4 flex flex-col gap-3">
        {onPreviewJson && (
          <button className={buttonClass} onClick={onPreviewJson}>
            {t("topology.preview_json")}
          </button>
        )}
        <button className={buttonClass} onClick={onImportClick}>
          {t("topology.import_topology")}
        </button>
        <input
          type="file"
          accept="application/json"
          ref={fileInputRef}
          onChange={onImportChange}
          style={{ display: "none" }}
        />
        <button className={buttonClass} onClick={onReset}>
          {t("topology.reset_canvas")}
        </button>
        {onSyncTopology && (
          <button
            className={`${buttonClass} ${!hasUnsyncedChanges || syncLoading ? "opacity-40 cursor-default pointer-events-none" : ""}`}
            onClick={
              hasUnsyncedChanges && !syncLoading ? onSyncTopology : undefined
            }
          >
            {syncLoading
              ? t("topology.sync.syncing")
              : t("topology.sync.sync_topology")}
          </button>
        )}
        <button
          className={`${buttonClass} ${submitLoading ? "opacity-70" : ""} ${submitSuccess ? "!border-green-500 !text-green-400" : ""} ${submitError ? "!border-red-500 !text-red-400" : ""}`}
          onClick={onSubmitTopology}
          disabled={submitLoading}
        >
          {submitLoading
            ? t("generic.submitting")
            : submitSuccess
              ? t("generic.submitted")
              : submitError
                ? t("generic.failed")
                : t("topology.submit_topology.submit")}
        </button>
      </div>
    </div>
  );
}

TopologyActionsPanel.propTypes = {
  fileInputRef: PropTypes.object,
  onPreviewJson: PropTypes.func,
  onImportClick: PropTypes.func,
  onImportChange: PropTypes.func,
  onReset: PropTypes.func,
  onSubmitTopology: PropTypes.func,
  onSyncTopology: PropTypes.func,
  hasUnsyncedChanges: PropTypes.bool,
  savedTopologies: PropTypes.arrayOf(PropTypes.object),
  defaultTopologies: PropTypes.arrayOf(PropTypes.object),
  onSavedTopologyClick: PropTypes.func,
  onDefaultTopologyClick: PropTypes.func,
  aiLoading: PropTypes.bool,
  horizontal: PropTypes.bool,
  submitLoading: PropTypes.bool,
  submitSuccess: PropTypes.bool,
  submitError: PropTypes.string,
  syncLoading: PropTypes.bool,
  isRevertPreview: PropTypes.bool,
  onRevertSave: PropTypes.func,
  onRevertUndo: PropTypes.func,
};

export default TopologyActionsPanel;
